/**
 * Add New Product — manual creation flow.
 *
 * Mirrors the URL-import "Review" UX: photos panel on the left, product
 * form panel on the right. Reuses the same shared components so the
 * field set, validation rules, and AI Color Change toggle stay in sync.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, AlertCircle, Loader2, Package } from 'lucide-react';
import toast from 'react-hot-toast';
import { inventoryAPI } from '../../api';
import InventoryProductForm, {
  validateProduct,
  isValid,
} from '../../components/inventory/InventoryProductForm';
import LocalPhotosPanel from '../../components/inventory/LocalPhotosPanel';
import { compressForCatalog } from '../../utils/imageUtils';

const blankProduct = () => ({
  product_name: '',
  sku_id: '',
  category: '',
  gender: 'female',
  dress_type: '',
  description: '',
  fabric: '',
  mrp: null,
  selling_price: null,
  currency: 'INR',
  color_variants: [],
  color_change_enabled: false,
  color_change_quality: 'standard',
  assigned_moment: null,
  additional_moments: [],
  size_chart: null,
  free_size: false,
  // Photo-panel state
  _local_files: [],
  _selected_images: [],
  _tryon_image_url: null,
});

const InventoryCreate = () => {
  const navigate = useNavigate();
  const [product, setProduct] = useState(blankProduct);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false); // shows errors only after first attempt

  const errors = useMemo(() => validateProduct(product), [product]);
  const photosError =
    (product._local_files || []).length === 0
      ? 'Add at least one photo'
      : errors.tryon_image_url || null;
  const canSave = isValid(product) && (product._local_files || []).length > 0;

  const update = (patch) => setProduct((prev) => ({ ...prev, ...patch }));

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!canSave) {
      toast.error('Please fill all required fields and add at least one photo');
      return;
    }
    setSubmitting(true);

    try {
      // 1. Create the product (no images yet)
      const createPayload = {
        sku_id: product.sku_id?.trim() || null,
        product_name: product.product_name.trim(),
        category: product.category?.trim() || null,
        gender: product.gender || 'female',
        dress_type: product.dress_type || null,
        description: product.description || null,
        fabric: product.fabric || null,
        mrp: product.mrp ?? null,
        selling_price: product.selling_price ?? null,
        currency: product.currency || 'INR',
        color: product.color_variants?.[0]?.name || null,
        color_variants: product.color_variants || [],
        color_change_enabled: !!product.color_change_enabled,
        color_change_quality: product.color_change_quality || 'standard',
        assigned_moment: product.assigned_moment || null,
        additional_moments: product.additional_moments || [],
        size_chart: product.free_size ? null : product.size_chart,
        free_size: !!product.free_size,
      };

      const createRes = await inventoryAPI.createProduct(createPayload);
      const productId = createRes.data.product_id;

      // 2. Upload each local file. Mark the chosen one as the try-on input.
      const files = product._local_files || [];
      const tryonId =
        product._tryon_image_url || (files.length === 1 ? files[0].id : null);

      let uploadFailures = 0;
      for (const f of files) {
        try {
          // Compress before upload so we stay under AWS API Gateway's
          // ~6 MB sync-invoke cap. Originals up to MAX_UPLOAD_MB are
          // accepted client-side; this helper trims them down to <4 MB.
          const compressed = await compressForCatalog(f.file);
          const fd = new FormData();
          fd.append('file', compressed, compressed.name || f.file.name);
          fd.append('is_tryon_input', String(f.id === tryonId));
          await inventoryAPI.uploadImage(productId, fd);
        } catch (e) {
          uploadFailures += 1;
          console.error('Image upload failed', e);
        }
      }

      if (uploadFailures === 0) {
        toast.success('Product created!');
      } else if (uploadFailures < files.length) {
        toast(`Product created. ${uploadFailures} image(s) failed to upload.`, { icon: '⚠️' });
      } else {
        toast.error('Product created but all image uploads failed. Add photos from the detail page.');
      }

      navigate(`/partners/inventory/${productId}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail?.message || JSON.stringify(detail)) : detail;
      toast.error(msg || 'Failed to create product');
    } finally {
      setSubmitting(false);
    }
  };

  // Field-level inline errors only show after the first submit attempt (avoids
  // a wall of red on first paint). The footer summary, however, is always
  // visible so the partner can see *why* the Create button is disabled.
  const visibleErrors = submitted ? errors : {};
  const visiblePhotosError = submitted ? photosError : null;

  // Compact human-readable list of missing required fields.
  const missingLabels = (() => {
    const labels = [];
    if ((product._local_files || []).length === 0) labels.push('Photo');
    if (!product.product_name?.trim()) labels.push('Product name');
    if (!product.gender) labels.push('Gender');
    if (!product.dress_type) labels.push('Dress type');
    if (!(product.color_variants?.length)) labels.push('Color variants');
    const sp = Number(product.selling_price);
    if (!sp || sp <= 0) labels.push('Selling price');
    if (!product.free_size) {
      const sc = product.size_chart;
      const hasRows = sc && typeof sc === 'object' && Object.keys(sc).length > 0;
      if (!hasRows) labels.push('Size chart');
    }
    if ((product._local_files || []).length > 1 && !product._tryon_image_url) {
      labels.push('Try-on photo');
    }
    return labels;
  })();

  return (
    <div className="space-y-4 max-w-7xl mx-auto" data-testid="inventory-create-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          to="/partners/inventory"
          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">Add New Product</h1>
          <p className="text-slate-500 text-sm">Photos on the left, product details on the right.</p>
        </div>
      </div>

      {/* Two-column layout — same as the URL-import review step */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-[calc(100vh-220px)] min-h-[560px]"
      >
        <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] flex-1 min-h-0">
          <LocalPhotosPanel
            product={product}
            onUpdate={update}
            validationError={visiblePhotosError}
          />
          <InventoryProductForm
            product={product}
            onUpdate={update}
            errors={visibleErrors}
          />
        </div>

        {/* Footer / save bar */}
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between gap-3 bg-slate-50">
          <div className="flex-1 min-w-0">
            {!canSave && missingLabels.length > 0 && (
              <p
                className="text-xs text-slate-600 flex items-start gap-1.5"
                data-testid="inventory-create-missing-fields"
              >
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 text-amber-500 mt-0.5" />
                <span>
                  <span className="font-medium text-slate-700">Required to enable Create:</span>{' '}
                  {missingLabels.join(', ')}
                </span>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => navigate('/partners/inventory')}
            className="px-4 py-2 text-sm border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !canSave}
            title={!canSave ? 'Fill all required fields and add at least one photo' : ''}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="inventory-create-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Creating…
              </>
            ) : (
              <>
                <Package className="w-4 h-4" />
                Create Product
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default InventoryCreate;
